import { type ButtonHTMLAttributes, type ReactElement } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap no-underline transition-all cursor-pointer disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-(--bp-accent) text-white border border-transparent hover:bg-(--bp-accent-dim)',
        outline: 'bg-white text-(--bp-ink2) border border-(--bp-border-s) hover:bg-(--bp-surface2)',
        destructive: 'bg-white text-(--bp-critical) border border-(--bp-critical-border) hover:bg-(--bp-critical-pale)',
        ghost: 'bg-transparent text-(--bp-ink2) border-none hover:bg-(--bp-surface2)'
      },
      size: {
        sm: 'gap-1.5 px-3.5 py-[7px] text-[13px] font-medium rounded-md',
        lg: 'gap-2 w-full py-2.75 px-5 text-sm font-semibold rounded-[7px] tracking-[-0.01em]'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm'
    }
  }
)

interface Props
  extends ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export function Button ({ className, variant, size, asChild = false, ...props }: Props): ReactElement {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { buttonVariants }
